import {
	BDSProvider,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	Grid,
	SearchField,
	Typography,
} from '@barracuda-internal/bds-core-v2';
import { Filter } from '@barracuda-internal/bds-core/dist/Icons/Core';
import React, { useState } from 'react';

type FilterType = { fieldName?: string; value?: string[]; operation?: string };

export type Option = {
	label: string;
	value: string;
};

interface DIInputCheckboxFilterProps {
	columnFilter?: { fieldName?: string; value?: string[]; operation?: string };
	columnId?: string;
	setColumnFilter?: (filter: FilterType) => void;
	operation?: string;
	searchPlaceholder?: string;
	selectAllLabel?: string;
	title?: string;
	checkboxOptions?: Option[];
	handleRemoveColumnFilter: (columnId: string) => void;
	showSelectAll?: boolean;
	optionCounts?: Record<string, number>;
}

const DIInputCheckboxFilter: React.FC<DIInputCheckboxFilterProps> = ({
	columnFilter,
	columnId = '',
	checkboxOptions = [],
	searchPlaceholder = 'Search',
	selectAllLabel = 'Select all',
	title = 'Filter',
	operation = '',
	setColumnFilter,
	handleRemoveColumnFilter,
	showSelectAll = true,
	optionCounts,
}) => {
	const [localCheckboxOptions, setLocalCheckboxOptions] =
		useState<Option[]>(checkboxOptions);
	const [checkboxOptionsChecked, setCheckboxOptionsChecked] = useState<
		string[]
	>(columnFilter?.value || []);
	const [isSearching, setIsSearching] = useState<boolean>(false);

	const handleInputFilterSearchChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const value = event.target.value.trim().toLowerCase();

		setIsSearching(value.length > 0);

		const filteredOptions = checkboxOptions.filter((option) =>
			option.label.toLowerCase().includes(value),
		);

		setLocalCheckboxOptions(filteredOptions);
	};

	const handleCheckboxChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const { value, checked } = event.target;

		const updatedChecked = checked
			? [...checkboxOptionsChecked, value]
			: checkboxOptionsChecked.filter(
					(optionValue) => optionValue !== value,
			  );

		setCheckboxOptionsChecked(updatedChecked);

		if (updatedChecked.length === 0) {
			handleRemoveColumnFilter(columnId);
		} else {
			setColumnFilter({
				fieldName: columnId,
				operation: operation,
				value: updatedChecked,
			});
		}
	};

	const handleCheckboxSelectAllChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const { checked } = event.target;

		const updatedChecked = checked
			? localCheckboxOptions.map((option) => option.value)
			: [];

		setCheckboxOptionsChecked(updatedChecked);

		if (updatedChecked.length === 0) {
			handleRemoveColumnFilter(columnId);
		} else {
			setColumnFilter({
				fieldName: columnId,
				operation: operation,
				value: updatedChecked,
			});
		}
	};

	return (
		<BDSProvider>
			<Grid container direction="column" spacing={2} padding={2}>
				<Grid item display="flex" alignItems="center">
					<Filter fontSize="small" color="action" />
					<Typography
						variant="subtitle1"
						fontSize="14px"
						pl={1}
						pt={0.5}
						fontWeight={700}
					>
						{title}
					</Typography>
				</Grid>

				<Grid item>
					<Grid
						container
						display="flex"
						justifyContent="flex-end"
						direction="column"
						spacing={2}
					>
						<Grid item>
							<SearchField
								placeholder={searchPlaceholder}
								onChange={handleInputFilterSearchChange}
							/>
						</Grid>
						<Grid
							item
							width={300}
							sx={{ maxHeight: 600, overflowY: 'auto' }}
						>
							<FormControl
								sx={(theme) => ({
									marginLeft: theme.spacing(1),
								})}
							>
								<FormGroup>
									{!isSearching && showSelectAll && (
										<FormControlLabel
											sx={{ padding: '5px' }}
											control={
												<Checkbox
													size="small"
													checked={
														checkboxOptionsChecked.length ===
															checkboxOptions.length &&
														checkboxOptions.length >
															0
													}
													indeterminate={
														checkboxOptionsChecked.length >
															0 &&
														checkboxOptionsChecked.length <
															checkboxOptions.length
													}
													onChange={
														handleCheckboxSelectAllChange
													}
													name="select all"
													sx={{
														marginRight: '5px',
													}}
												/>
											}
											label={
												<Typography variant="body2">
													{selectAllLabel}
												</Typography>
											}
										/>
									)}
									{localCheckboxOptions.map(
										(option: Option) => {
											const count =
												optionCounts?.[option.value];
											const isDisabled =
												typeof count === 'number' &&
												count === 0;
											return (
												<FormControlLabel
													key={option.value}
													sx={{ padding: '5px' }}
													control={
														<Checkbox
															size="small"
															color="primary"
															checked={
																checkboxOptionsChecked.indexOf(
																	option.value,
																) > -1
															}
															onChange={
																handleCheckboxChange
															}
															name={option.value}
															value={option.value}
															sx={{
																marginRight:
																	'5px',
															}}
															disabled={
																isDisabled
															}
														/>
													}
													label={
														<Typography
															variant="body2"
															sx={{
																color:
																	typeof count ===
																		'number' &&
																	count === 0
																		? '#888'
																		: 'inherit',
															}}
														>
															{option.label}
															{typeof count ===
																'number' &&
																` (${count})`}
														</Typography>
													}
												/>
											);
										},
									)}
								</FormGroup>
							</FormControl>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</BDSProvider>
	);
};

export default DIInputCheckboxFilter;
